import React, {useState} from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  Platform,
} from 'react-native';
import { COLORS } from '../theme/themeTokens';
import { COLORS_WRONG } from '../theme/colorsWrong';

const FONTS = {
  title: Platform.select({
    ios: 'HelveticaNeue',
    android: 'sans-serif',
  }),
  body: Platform.select({
    ios: 'System',
    android: 'sans-serif',
  }),
};

interface SignUpScreenProps {
  onSignUp: () => void;
  onLogin: () => void;
}

export const SignUpScreen: React.FC<SignUpScreenProps> = ({
  onSignUp,
  onLogin,
}) => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const handleSignUp = () => {
    onSignUp();
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled">
        
        {/* Top Section */}
        <View style={styles.topSection}>
          {/* Title */}
          <Text style={styles.title}>Hello!</Text>

          {/* Form Section */}
          <View style={styles.formSection}>
            {/* Input Fields */}
            <View style={styles.inputContainer}>
              {/* Login Input */}
              <View style={styles.inputWrapper}>
                <TextInput
                  style={styles.input}
                  placeholder="Login"
                  placeholderTextColor={COLORS.blueSteel}
                  value={login}
                  onChangeText={setLogin}
                  autoCapitalize="none"
                />
              </View>

              {/* Password Input */}
              <View style={[styles.inputWrapper, styles.passwordWrapper]}>
                <TextInput
                  style={styles.input}
                  placeholder="Password"
                  placeholderTextColor={COLORS.blueSteel}
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                />
                <TouchableOpacity
                  style={styles.eyeButton}
                  onPress={() => setShowPassword(!showPassword)}>
                  <View style={styles.eyeIconContainer}>
                    {/* Eye shape using border */}
                    <View style={styles.eyeShape} />
                    {/* Pupil */}
                    <View style={styles.eyePupil} />
                  </View>
                </TouchableOpacity>
              </View>

              {/* Email Input */}
              <View style={styles.inputWrapper}>
                <TextInput
                  style={styles.input}
                  placeholder="Email"
                  placeholderTextColor={COLORS.blueSteel}
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </View>
            </View>

            {/* Terms Checkbox */}
            <TouchableOpacity
              style={styles.termsContainer}
              onPress={() => setAgreeTerms(!agreeTerms)}>
              <View
                style={[
                  styles.checkbox,
                  agreeTerms && styles.checkboxChecked,
                ]}>
                {agreeTerms && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.termsText}>
                I agree with{' '}
                <Text style={styles.termsLink}>Terms</Text>
                {' '}and{' '}
                <Text style={styles.termsLink}>Privacy Policy</Text>
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Bottom Section */}
        <View style={styles.bottomSection}>
          {/* Sign Up Button */}
          <TouchableOpacity style={styles.signUpButton} onPress={handleSignUp}>
            <Text style={styles.signUpButtonText}>Sign up</Text>
          </TouchableOpacity>

          {/* Or Divider */}
          <Text style={styles.orText}>or</Text>

          {/* Social Buttons */}
          <View style={styles.socialContainer}>
            <TouchableOpacity style={styles.socialButton}>
              <View style={styles.socialIconContainer}>
                <Text style={styles.googleIcon}>G</Text>
              </View>
              <Text style={styles.socialButtonText}>Sign up with Google</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.socialButton}>
              <View style={styles.socialIconContainer}>
                <Text style={styles.appleIcon}></Text>
              </View>
              <Text style={styles.socialButtonText}>Sign up with Apple</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Login Link */}
        <View style={styles.loginContainer}>
          <Text style={styles.loginText}>
            Already have an account?{' '}
            <Text style={styles.loginLink} onPress={onLogin}>
              Login
            </Text>
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 80,
    gap: 60,
  },
  topSection: {
    gap: 24,
  },
  title: {
    fontFamily: FONTS.title,
    fontSize: 48,
    fontWeight: '400',
    lineHeight: 57,
    color: COLORS.deepNavy,
  },
  formSection: {
    gap: 20,
  },
  inputContainer: {
    gap: 12,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.blueSteel,
    borderRadius: 4,
    height: 60,
    paddingHorizontal: 20,
  },
  passwordWrapper: {
    height: 64,
  },
  input: {
    flex: 1,
    fontFamily: FONTS.body,
    fontSize: 18,
    lineHeight: 19,
    color: COLORS.deepNavy,
    height: '100%',
  },
  eyeButton: {
    padding: 4,
  },
  eyeIconContainer: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  eyeShape: {
    width: 22,
    height: 16,
    borderWidth: 1,
    borderColor: COLORS.blueSteel,
    borderRadius: 11,
  },
  eyePupil: {
    position: 'absolute',
    width: 6,
    height: 6,
    borderWidth: 1,
    borderColor: COLORS.blueSteel,
    borderRadius: 3,
  },
  termsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: COLORS.blueSteel,
    borderRadius: 2,
    backgroundColor: COLORS.white,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: COLORS.brandOrange,
    borderColor: COLORS.brandOrange,
  },
  checkmark: {
    color: COLORS.white,
    fontSize: 14,
    fontWeight: '600',
  },
  termsText: {
    fontFamily: FONTS.body,
    fontSize: 14,
    lineHeight: 17,
    color: COLORS.deepNavy,
  },
  termsLink: {
    color: COLORS.brandOrange,
  },
  bottomSection: {
    gap: 24,
  },
  signUpButton: {
    backgroundColor: COLORS.brandOrange,
    borderRadius: 4,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  signUpButtonText: {
    fontFamily: 'Inter',
    color: COLORS.white,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
  },
  orText: {
    fontFamily: FONTS.body,
    fontSize: 16,
    lineHeight: 17,
    color: COLORS.blueSteel,
    textAlign: 'center',
  },
  socialContainer: {
    gap: 12,
  },
  socialButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.brandOrange,
    borderRadius: 4,
    height: 54,
    paddingHorizontal: 53,
    paddingVertical: 15,
    gap: 10,
  },
  socialIconContainer: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  googleIcon: {
    fontSize: 20,
    fontWeight: '700',
    color: COLORS_WRONG.googleBlue,
  },
  appleIcon: {
    fontSize: 22,
    color: COLORS_WRONG.googleDark,
  },
  socialButtonText: {
    fontFamily: FONTS.body,
    fontSize: 20,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS_WRONG.keypadText,
  },
  loginContainer: {
    alignItems: 'center',
  },
  loginText: {
    fontFamily: FONTS.body,
    fontSize: 18,
    lineHeight: 19,
    color: COLORS.deepNavy,
    textAlign: 'center',
  },
  loginLink: {
    color: COLORS.brandOrange,
  },
});

export default SignUpScreen;
